import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './CollegeCard.module.css';
import { getThumbnail, getAllImageFormats } from '../utils/collegeImageMapper';
import { motion } from 'framer-motion';

const CollegeCard = ({ college, onClick, onRemove }) => {
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Get all possible image formats for this college
  const imageFormats = getAllImageFormats(college.name);
  const currentImageSrc = imageFormats[currentImageIndex] || getThumbnail(college.name);
  
  // Fallback to default image if no formats are available
  const finalImageSrc = imageFormats.length > 0 ? currentImageSrc : getThumbnail(college.name);
  
  const acceptanceRate = college.stats?.acceptanceRate || college.acceptanceRate || 'N/A';
  const location = college.location || 'N/A';
  
  const handleImageError = () => {
    // Try next image format
    if (currentImageIndex < imageFormats.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    } else {
      // If all formats fail, use default
      setImageError(true);
      setCurrentImageIndex(0); // Reset to try again if component re-renders
    }
  };
  
  const handleCardClick = () => {
    const scrollData = {
      position: window.scrollY,
      timestamp: Date.now(),
      path: window.location.pathname
    };
    sessionStorage.setItem('exploreScrollPosition', JSON.stringify(scrollData));
    if (onClick) onClick();
  };

  const handleRemoveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRemove) {
      onRemove();
    }
  };

  const hoverVariants = {
    hover: {
      y: -4,
      scale: 1,
      rotateX: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  // Helper function to get circle color based on acceptance rate
  const getCircleColor = (rate) => {
    const numRate = parseFloat(rate);
    if (numRate < 10) return '#ff6b6b'; // Red for very competitive
    if (numRate < 30) return '#feca57'; // Yellow for competitive  
    return '#48cab2'; // Green for less competitive
  };

  return (
    <motion.div
      variants={hoverVariants}
      whileHover="hover"
      className={styles.cardWrapper}
    >
      <Link to={`/college-profile/${college.name}`} className={styles.cardLink} onClick={handleCardClick}>
        <div className={styles.card}>
          <div className={styles.arrowTopRight}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="12" fill="#3A9AF9"/>
              <path d="M8 12h8m0 0l-3-3m3 3l-3 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className={styles.imageContainer}>
            <img 
              src={finalImageSrc} 
              alt={`${college.name} thumbnail`} 
              className={styles.image}
              onError={handleImageError}
              loading="lazy"
              onLoad={() => setImageError(false)} // Reset error state on successful load
            />
          </div>
          <div className={styles.cardBody}>
            <h3 className={styles.title}>{college.name}</h3>
            <div className={styles.infoRow}>
              <span className={styles.location}>{location}</span>
              <div className={styles.acceptanceContainer}>
                <svg width="50" height="50" viewBox="0 0 50 50" className={styles.acceptanceCircle}>
                  <circle
                    cx="25"
                    cy="25"
                    r="20"
                    fill="none"
                    stroke={getCircleColor(acceptanceRate)}
                    strokeWidth="2"
                    strokeDasharray={`${2 * Math.PI * 20}`}
                    strokeDashoffset={`${2 * Math.PI * 20 * (1 - (parseFloat(acceptanceRate) || 0) / 100)}`}
                    strokeLinecap="round"
                    style={{ 
                      transform: 'rotate(-90deg)', 
                      transformOrigin: '25px 25px',
                      transition: 'stroke-dashoffset 0.6s ease'
                    }}
                  />
                </svg>
                <span className={styles.acceptanceRate}>
                  {acceptanceRate !== 'N/A' ? `${acceptanceRate}%` : 'N/A'}
                </span>
              </div>
            </div>
          </div>
          {/* Remove button at bottom of card */}
          {onRemove && (
            <button
              className={styles.removeBtn}
              title={`Remove ${college.name} from your list`}
              onClick={handleRemoveClick}
              aria-label={`Remove ${college.name} from your list`}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="12" fill="#dc2626"/>
                <path d="M16 8L8 16M8 8L16 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

export default CollegeCard;